import type { IssueDetail } from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useIssueDetail, useIssueList, useIssueMgmtWrite } from '../../hooks/useIssueMgmt.js';
import { IssueStatusBadge } from './IssueStatusBadge.js';
import { useIssueToast } from './useIssueToast.js';

/**
 * 検索を投げるまで待つ時間。
 * @graph-connects none
 */
const SEARCH_DELAY_MS = 300;

/**
 * 「別の課題にまとめる」の小窓。まとめ先を番号・言葉で探して1つ選ぶ。
 * 自分自身と、自分へ統合済みの課題は候補に出さない（統合済みの課題は一覧に出ないので候補にも出ない）。
 * まとめ先の版は選んだときに詳細から読み、まとめる間に他の人が変えていたら中止される。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 同じ課題を1つにまとめる小窓。まとめ先を探して選び、この課題の声・案件・テーマをまとめ先へ移す
 * @graph-connects useIssueList [calls] まとめ先の候補
 * @graph-connects useIssueDetail [calls] まとめ先の版
 * @graph-connects useIssueMgmtWrite [calls] merge
 * @graph-connects useIssueToast [calls] 保存の知らせ
 */
export function MergeDialog({ issue, onClose }: { issue: IssueDetail; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const merge = useIssueMgmtWrite('merge');
  const [query, setQuery] = useState('');
  const [targetId, setTargetId] = useState<string | null>(null);
  const debounced = useDebouncedValue(query.trim(), SEARCH_DELAY_MS);
  const list = useIssueList({
    query: debounced,
    themeId: null,
    includeHidden: false,
    unlinkedLimit: 1,
  });
  const target = useIssueDetail(targetId ?? '', targetId !== null);
  const excluded = new Set([issue.id, ...issue.mergedFrom.map((m) => m.id)]);
  const candidates = (list.data?.issues ?? []).filter((row) => !excluded.has(row.id));
  const ready = target.data !== undefined && target.data.id === targetId;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={t('issueMgmt.merge.title', { title: issue.title })}
      description={t('issueMgmt.merge.description')}
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-xs text-[var(--color-text-muted)]">
            {targetId === null ? t('issueMgmt.merge.hint') : ''}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              {t('issueMgmt.common.cancel')}
            </Button>
            <Button
              disabled={!ready}
              loading={merge.isPending}
              onClick={() => {
                if (target.data === undefined) return;
                const targetTitle = target.data.title;
                merge.mutate(
                  {
                    sourceId: issue.id,
                    targetId: target.data.id,
                    sourceRevision: issue.revision,
                    targetRevision: target.data.revision,
                  },
                  {
                    onSuccess: (result) => {
                      onClose();
                      toast.notifySaved(
                        t('issueMgmt.merge.saved', { title: issue.title, target: targetTitle }),
                        { operationId: result.operationId },
                      );
                    },
                    onError: toast.notifyError,
                  },
                );
              }}
            >
              {t('issueMgmt.common.decide')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <Input
          aria-label={t('issueMgmt.merge.search')}
          placeholder={t('issueMgmt.merge.search')}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
        />
        <div
          role="radiogroup"
          aria-label={t('issueMgmt.merge.candidates')}
          className="max-h-80 divide-y divide-[var(--color-border)] overflow-y-auto rounded-lg border border-[var(--color-border)]"
        >
          {list.data !== undefined && candidates.length === 0 && (
            <p className="px-3 py-4 text-sm text-[var(--color-text-muted)]">
              {t('issueMgmt.merge.empty')}
            </p>
          )}
          {candidates.map((row) => (
            <button
              key={row.id}
              type="button"
              role="radio"
              aria-checked={row.id === targetId}
              onClick={() => {
                setTargetId(row.id);
              }}
              className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm ${
                row.id === targetId
                  ? 'bg-[color-mix(in_srgb,var(--color-accent-primary)_10%,transparent)]'
                  : 'hover:bg-[var(--color-bg-hover)]'
              }`}
            >
              <span className="text-xs text-[var(--color-text-muted)]">{row.id}</span>
              <span className="flex-1 font-medium text-[var(--color-text-primary)]">
                {row.title}
              </span>
              <IssueStatusBadge status={row.displayStatus} />
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
